<template>
  <div v-if="open" class="switchers-overlay" @click.self="close">
    <div class="switchers-modal" :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'">
      <header class="switchers-header">
        <h3>{{ $t('aiAssistant.switchers.title') }}</h3>
        <button class="close-btn" :aria-label="$t('aiAssistant.close')" @click="close">&times;</button>
      </header>

      <div class="switchers-body">
        <p class="switchers-hint">{{ $t('aiAssistant.switchers.hint') }}</p>

        <label class="switchers-field">
          <span>{{ $t('aiAssistant.switchers.typeLabel') }}</span>
          <select v-model="type" :disabled="busy">
            <option value="zid_reviews">{{ $t('aiAssistant.switchers.types.zid_reviews') }}</option>
            <option value="zid_orders">{{ $t('aiAssistant.switchers.types.zid_orders') }}</option>
            <option value="salla_products">{{ $t('aiAssistant.switchers.types.salla_products') }}</option>
          </select>
        </label>

        <label class="switchers-field">
          <span>{{ $t('aiAssistant.switchers.fileLabel') }}</span>
          <input
            ref="fileInput"
            type="file"
            accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            :disabled="busy"
            @change="onFileChange"
          />
          <small v-if="file">{{ file.name }} ({{ formatBytes(file.size) }})</small>
        </label>

        <label class="switchers-field">
          <span>{{ $t('aiAssistant.switchers.errorLabel') }}</span>
          <textarea
            v-model="errorMessage"
            rows="3"
            :placeholder="$t('aiAssistant.switchers.errorPlaceholder')"
            :disabled="busy"
          ></textarea>
        </label>

        <div v-if="errorText" class="switchers-error">{{ errorText }}</div>

        <div v-if="result" class="switchers-result">
          <div class="result-row">
            <strong>{{ $t('aiAssistant.switchers.before') }}:</strong>
            {{ $t('aiAssistant.switchers.rowsWithErrors', { count: result.before.errorRows, total: result.before.totalRows }) }}
          </div>
          <div class="result-row">
            <strong>{{ $t('aiAssistant.switchers.after') }}:</strong>
            {{ $t('aiAssistant.switchers.rowsWithErrors', { count: result.after.errorRows, total: result.output_rows }) }}
          </div>
          <details class="result-explanation">
            <summary>{{ $t('aiAssistant.switchers.whatChanged') }}</summary>
            <p>{{ result.plan.explanation }}</p>
          </details>
          <button class="primary-btn" :disabled="busy" @click="downloadFixed">
            {{ $t('aiAssistant.switchers.downloadFixed', { name: result.filename }) }}
          </button>
        </div>
      </div>

      <footer class="switchers-footer">
        <button class="text-btn" :disabled="busy" @click="close">
          {{ $t('aiAssistant.switchers.close') }}
        </button>
        <button
          class="primary-btn"
          :disabled="busy || !file"
          @click="submit"
        >
          {{ busy ? $t('aiAssistant.switchers.working') : $t('aiAssistant.switchers.validateAndFix') }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
export default {
  name: "SwitchersFixPanel",
  props: {
    open: { type: Boolean, default: false },
  },
  data() {
    return {
      type: "zid_reviews",
      file: null,
      errorMessage: "",
      busy: false,
      errorText: "",
      result: null,
    };
  },
  computed: {
    baseUrl() {
      return import.meta.env.VITE_KHABEER_AI_URL || "";
    },
    token() {
      return this.$store.state.user?.accessToken ?? "";
    },
  },
  watch: {
    open(v) {
      if (!v) this.reset();
    },
  },
  methods: {
    reset() {
      this.file = null;
      this.errorMessage = "";
      this.errorText = "";
      this.result = null;
      this.busy = false;
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
    close() {
      this.$emit("close");
    },
    onFileChange(e) {
      this.file = e.target.files?.[0] ?? null;
      this.result = null;
      this.errorText = "";
    },
    formatBytes(n) {
      if (n < 1024) return `${n} B`;
      if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
      return `${(n / 1024 / 1024).toFixed(1)} MB`;
    },
    async submit() {
      if (!this.file) return;
      this.busy = true;
      this.errorText = "";
      this.result = null;
      try {
        const fd = new FormData();
        fd.append("type", this.type);
        fd.append("file", this.file);
        if (this.errorMessage.trim()) fd.append("backend_error_message", this.errorMessage.trim());

        const res = await fetch(`${this.baseUrl}/api/switchers/validate-fix`, {
          method: "POST",
          headers: { "X-OFFICER-TOKEN": this.token },
          body: fd,
        });

        if (!res.ok) {
          const body = await res.text();
          throw new Error(`HTTP ${res.status}: ${body.slice(0, 300)}`);
        }
        this.result = await res.json();
      } catch (err) {
        this.errorText = err?.message || String(err);
      } finally {
        this.busy = false;
      }
    },
    async downloadFixed() {
      if (!this.result) return;
      try {
        const res = await fetch(`${this.baseUrl}${this.result.download_url}`, {
          method: "GET",
          headers: { "X-OFFICER-TOKEN": this.token },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = this.result.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(a.href);
      } catch (err) {
        this.errorText = `Download failed: ${err.message || err}`;
      }
    },
  },
};
</script>

<style scoped>
.switchers-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.switchers-modal {
  background: #fff;
  width: min(560px, 92vw);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  box-shadow: 0 25px 55px -10px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}
.switchers-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid #e5e7eb;
}
.switchers-header h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
}
.close-btn {
  background: transparent;
  border: 0;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  color: #6b7280;
}
.switchers-body {
  padding: 18px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.switchers-hint {
  font-size: 13px;
  color: #4b5563;
  margin: 0;
  line-height: 1.5;
}
.switchers-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
}
.switchers-field span {
  font-weight: 500;
  color: #374151;
}
.switchers-field select,
.switchers-field input[type="file"],
.switchers-field textarea {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
}
.switchers-field small {
  color: #6b7280;
  font-size: 12px;
}
.switchers-error {
  background: #fee2e2;
  color: #991b1b;
  padding: 10px;
  border-radius: 6px;
  font-size: 13px;
  white-space: pre-wrap;
}
.switchers-result {
  background: #f1f5f9;
  border-radius: 6px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: 13px;
}
.result-row {
  color: #1f2937;
}
.result-explanation {
  background: #fff;
  border-radius: 4px;
  padding: 8px 10px;
}
.result-explanation summary {
  cursor: pointer;
  font-weight: 500;
}
.result-explanation p {
  margin: 8px 0 0;
  line-height: 1.5;
  color: #374151;
}
.switchers-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 18px;
  border-top: 1px solid #e5e7eb;
}
.text-btn {
  background: transparent;
  border: 0;
  padding: 8px 12px;
  cursor: pointer;
  color: #4b5563;
  border-radius: 6px;
}
.text-btn:hover {
  background: #f3f4f6;
}
.primary-btn {
  background: #111827;
  color: #fff;
  border: 0;
  padding: 8px 14px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
}
.primary-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
